import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Export the current journey to a clean, readable PDF.
 * - Sets window.__PDF_EXPORTING__ so InfoSpot tooltips pop open for the capture.
 * - Lets each scene settle before screenshotting.
 * - Produces A4 landscape, one scene per page, with title and step count.
 *
 * Clipping-fix strategy:
 *   1. Add CSS class `.pdf-exporting` to body — forces all portal shells to
 *      height:auto via stylesheet !important rules (App.css).
 *   2. Before each capture, additionally stamp `style="... !important"` on
 *      every relevant element so framer-motion inline styles can't win.
 *   3. Temporarily set the card's own inline height/maxHeight/overflow.
 *   4. Capture with html2canvas using the node's scrollWidth/scrollHeight.
 */
export async function exportJourneyToPdf({ journeyTitle, scenes, gotoStep, stepDelayMs = 2600 }) {
  if (typeof window !== 'undefined') window.__PDF_EXPORTING__ = true;
  document.body.classList.add('pdf-exporting');
  await new Promise(r => setTimeout(r, 500));

  /**
   * Force every PortalShell (v1 + v2) and its descendants to natural height
   * and visible overflow. Returns a restore function that reverts the inline
   * style mutations once the capture is done.
   */
  const forceExpansion = () => {
    const mutations = [];
    const force = (el, k, v) => {
      mutations.push([el, k, el.style.getPropertyValue(k), el.style.getPropertyPriority(k)]);
      el.style.setProperty(k, v, 'important');
    };

    // scene-container is fixed-positioned; neutralise it.
    document.querySelectorAll('.scene-container').forEach(el => {
      force(el, 'position', 'static');
      force(el, 'top', 'auto');
      force(el, 'bottom', 'auto');
      force(el, 'height', 'auto');
      force(el, 'overflow', 'visible');
    });

    // Every portal-shell card — strip height caps and overflow clipping.
    document.querySelectorAll('[data-testid="portal-shell"]').forEach(root => {
      force(root, 'height', 'auto');
      force(root, 'max-height', 'none');
      force(root, 'min-height', '0');
      force(root, 'overflow', 'visible');
      // Every descendant with overflow or min-height constraints.
      root.querySelectorAll('*').forEach(inner => {
        const cs = getComputedStyle(inner);
        if (cs.overflow === 'hidden' || cs.overflow === 'auto' ||
            cs.overflowY === 'auto' || cs.overflowY === 'hidden' ||
            cs.overflowY === 'scroll') {
          force(inner, 'overflow', 'visible');
          force(inner, 'overflow-y', 'visible');
          force(inner, 'overflow-x', 'visible');
        }
        if (cs.maxHeight !== 'none') force(inner, 'max-height', 'none');
        if (cs.minHeight === '0px' || inner.classList.contains('min-h-0')) {
          force(inner, 'min-height', 'auto');
        }
        // framer-motion can leave transform: translate(...) that shifts
        // elements out of capture — only reset translateY when this is a
        // scene-level wrapper (scene-wrapper) to avoid breaking intended
        // decorative transforms.
      });
    });
    return () => mutations.forEach(([el, k, val, prio]) => {
      if (val) el.style.setProperty(k, val, prio);
      else el.style.removeProperty(k);
    });
  };

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();   // 842
  const pageH = pdf.internal.pageSize.getHeight();  // 595
  const margin = 24;

  /* ------------------------- Cover page ------------------------- */
  pdf.setFillColor(13, 148, 136);
  pdf.rect(0, 0, pageW, 12, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(32);
  pdf.setTextColor(10, 10, 10);
  pdf.text('Shafafiya Walkthrough', margin, 120);

  pdf.setFontSize(20);
  pdf.setTextColor(13, 148, 136);
  pdf.text(journeyTitle, margin, 155);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(120, 120, 120);
  pdf.text(
    `Generated ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}  •  ${scenes.length} scenes`,
    margin, 178
  );

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(40, 40, 40);
  pdf.text('Scenes in this guide', margin, 225);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(40, 40, 40);
  const colW = (pageW - margin * 2) / 2;
  scenes.forEach((s, i) => {
    const col = i >= Math.ceil(scenes.length / 2) ? 1 : 0;
    const row = col === 0 ? i : i - Math.ceil(scenes.length / 2);
    const x = margin + col * colW;
    const yy = 250 + row * 20;
    if (yy > pageH - margin) return;
    pdf.text(`${String(i + 1).padStart(2, '0')}.  ${s.label}`, x, yy);
  });

  pdf.setFontSize(9);
  pdf.setTextColor(160, 160, 160);
  pdf.text('Interactive training demo — Shafafiya Health Insurance Exchange', margin, pageH - 20);

  /* ------------------------- Scene pages ------------------------- */
  for (let i = 0; i < scenes.length; i++) {
    gotoStep(i);
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, stepDelayMs));

    // Capture the inner card of whichever portal-shell is visible.
    const shell = document.querySelector('[data-testid="portal-shell"]');
    const node = shell || document.querySelector('.scene-wrapper');
    if (!node) continue;

    // Force expansion (stylesheet + inline !important mutation).
    const restore = forceExpansion();

    // Scroll all scrollable ancestors to top so nothing hidden above fold.
    document.querySelectorAll('[data-testid="portal-shell"], .overflow-y-auto').forEach(el => {
      el.scrollTop = 0;
    });
    window.scrollTo(0, 0);

    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, 550));

    const fullW = node.scrollWidth;
    const fullH = node.scrollHeight;

    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(node, {
      backgroundColor: '#FFFFFF',
      scale: 1.6,
      useCORS: true,
      logging: false,
      allowTaint: false,
      windowWidth: fullW,
      windowHeight: fullH,
      width: fullW,
      height: fullH,
      scrollX: 0,
      scrollY: 0,
    });

    restore();

    const img = canvas.toDataURL('image/jpeg', 0.92);

    pdf.addPage();

    pdf.setFillColor(13, 148, 136);
    pdf.rect(0, 0, pageW, 6, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(10, 10, 10);
    pdf.text(journeyTitle, margin, 26);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(95, 99, 104);
    pdf.text(`${scenes[i].label}`, margin, 42);

    pdf.setFontSize(10);
    pdf.setTextColor(95, 99, 104);
    pdf.text(`${i + 1} / ${scenes.length}`, pageW - margin, 26, { align: 'right' });

    // Image area — below header, above footer.
    const imgTop = 56;
    const imgBottom = pageH - 30;
    const availableH = imgBottom - imgTop;
    const availableW = pageW - margin * 2;

    const ratio = canvas.width / canvas.height;
    let drawW = availableW;
    let drawH = drawW / ratio;
    if (drawH > availableH) {
      drawH = availableH;
      drawW = drawH * ratio;
    }
    const x = (pageW - drawW) / 2;
    const yImg = imgTop + (availableH - drawH) / 2;

    pdf.addImage(img, 'JPEG', x, yImg, drawW, drawH, undefined, 'FAST');

    pdf.setFontSize(8);
    pdf.setTextColor(160, 160, 160);
    pdf.text('Shafafiya — interactive training demo', margin, pageH - 14);
    pdf.text(new Date().toLocaleDateString(), pageW - margin, pageH - 14, { align: 'right' });
  }

  if (typeof window !== 'undefined') window.__PDF_EXPORTING__ = false;
  document.body.classList.remove('pdf-exporting');

  const safe = journeyTitle.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  pdf.save(`shafafiya-${safe}.pdf`);
}
